import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { ARTISAN_SPECIALITES } from "@/lib/constants";
import { getSpecialtyCodesForMetier } from "@/lib/handymanConfig";

export interface ArtisanSearchResult {
  id: string;
  user_id: string;
  type: string;
  specialite: string;
  wilaya: string;
  commune: string;
  raison_sociale: string | null;
  description: string | null;
  logo_url: string | null;
  phone: string | null;
  availability: string;
  is_reactive_badge: boolean | null;
  profile_completion_score: number | null;
  response_rate: number | null;
  avg_response_time_minutes: number | null;
  last_login_at: string | null;
  communes_couvertes: string[] | null;
  verification_status: string | null;
  // joined from profiles
  display_name: string;
  // handyman fields
  is_polyvalent: boolean;
  handyman_specialties?: string[]; // specialty_codes pour les artisans polyvalents
}

export type SortOption = "relevance" | "response" | "recent";

// Re-export from constants so existing imports keep working.
export const specialites = ARTISAN_SPECIALITES;

export function useSearchArtisans(filters: {
  wilaya?: string;
  commune?: string;
  specialite?: string;
  handymanSpecialty?: string; // filtre sous-spécialité pour "Artisan polyvalent"
  search?: string;
  sort?: SortOption;
}) {
  return useQuery({
    queryKey: ["search-artisans", filters],
    queryFn: async () => {
      const isHandymanFilter = filters.specialite === "Artisan polyvalent";
      const isSpecialistFilter =
        filters.specialite &&
        filters.specialite !== "tous" &&
        !isHandymanFilter;

      // ── Cas 1 : filtre par métier spécialiste (Plomberie / Électricité / Serrurerie) ──
      if (isSpecialistFilter && filters.specialite) {
        return await fetchSpecialistResults(filters, filters.specialite);
      }

      // ── Cas 2 : filtre "Artisan polyvalent" ──
      if (isHandymanFilter) {
        return await fetchHandymanResults(filters);
      }

      // ── Cas 3 : pas de filtre spécialité → tous les artisans ──
      return await fetchAllResults(filters);
    },
  });
}

// ---------------------------------------------------------------------------
// Cas 1 : Spécialiste + cross-visibilité polyvalents
// ---------------------------------------------------------------------------
async function fetchSpecialistResults(
  filters: Parameters<typeof useSearchArtisans>[0],
  specialite: string
) {
  // Q1 : spécialistes directs
  let q1 = supabase
    .from("artisan_profiles")
    .select("*")
    .eq("is_active", true)
    .eq("availability", "disponible")
    .eq("specialite", specialite);
  if (filters.wilaya) q1 = q1.eq("wilaya", filters.wilaya);
  if (filters.commune) q1 = q1.eq("commune", filters.commune);
  const { data: specialists, error: e1 } = await q1;
  if (e1) throw e1;

  // Q2 : trouver les polyvalents ayant une sous-spécialité correspondante
  const matchingCodes = getSpecialtyCodesForMetier(specialite);
  let polyvalents: any[] = [];

  if (matchingCodes.length > 0) {
    const { data: hsRows, error: e2 } = await supabase
      .from("artisan_handyman_specialties")
      .select("artisan_id, specialty_code")
      .in("specialty_code", matchingCodes);
    if (e2) throw e2;

    if (hsRows && hsRows.length > 0) {
      const artisanIds = [...new Set(hsRows.map((r: any) => r.artisan_id))];
      let q3 = supabase
        .from("artisan_profiles")
        .select("*")
        .eq("is_active", true)
        .eq("availability", "disponible")
        .eq("specialite", "Artisan polyvalent")
        .in("id", artisanIds);
      if (filters.wilaya) q3 = q3.eq("wilaya", filters.wilaya);
      if (filters.commune) q3 = q3.eq("commune", filters.commune);
      const { data: polyvalentRows, error: e3 } = await q3;
      if (e3) throw e3;

      // Enrichir avec les codes de sous-spécialités
      const codesByArtisan = new Map<string, string[]>();
      hsRows.forEach((r: any) => {
        const existing = codesByArtisan.get(r.artisan_id) ?? [];
        existing.push(r.specialty_code);
        codesByArtisan.set(r.artisan_id, existing);
      });

      polyvalents = (polyvalentRows ?? []).map((a: any) => ({
        ...a,
        is_polyvalent: true,
        handyman_specialties: codesByArtisan.get(a.id) ?? [],
      }));
    }
  }

  const specialistsMapped = (specialists ?? []).map((a: any) => ({
    ...a,
    is_polyvalent: false,
  }));

  const allArtisans = [...specialistsMapped, ...polyvalents];
  if (allArtisans.length === 0) return [];

  const withNames = await joinDisplayNames(allArtisans);
  return processResults(withNames, filters, true) as ArtisanSearchResult[];
}

// ---------------------------------------------------------------------------
// Cas 2 : Filtre "Artisan polyvalent" uniquement
// ---------------------------------------------------------------------------
async function fetchHandymanResults(filters: Parameters<typeof useSearchArtisans>[0]) {
  let q = supabase
    .from("artisan_profiles")
    .select("*")
    .eq("is_active", true)
    .eq("availability", "disponible")
    .eq("specialite", "Artisan polyvalent");
  if (filters.wilaya) q = q.eq("wilaya", filters.wilaya);
  if (filters.commune) q = q.eq("commune", filters.commune);

  const { data: artisans, error } = await q;
  if (error) throw error;
  if (!artisans || artisans.length === 0) return [];

  // Charger les sous-spécialités pour filtrer / afficher
  const artisanIds = artisans.map((a: any) => a.id);
  let hsQuery = supabase
    .from("artisan_handyman_specialties")
    .select("artisan_id, specialty_code")
    .in("artisan_id", artisanIds);
  if (filters.handymanSpecialty) {
    hsQuery = hsQuery.eq("specialty_code", filters.handymanSpecialty);
  }
  const { data: hsRows } = await hsQuery;

  const codesByArtisan = new Map<string, string[]>();
  (hsRows ?? []).forEach((r: any) => {
    const existing = codesByArtisan.get(r.artisan_id) ?? [];
    existing.push(r.specialty_code);
    codesByArtisan.set(r.artisan_id, existing);
  });

  // Si un filtre sous-spécialité est actif, on ne garde que les artisans concernés
  const filtered = filters.handymanSpecialty
    ? artisans.filter((a: any) => codesByArtisan.has(a.id))
    : artisans;

  const mapped = filtered.map((a: any) => ({
    ...a,
    is_polyvalent: true,
    handyman_specialties: codesByArtisan.get(a.id) ?? [],
  }));

  const withNames = await joinDisplayNames(mapped);
  return processResults(withNames, filters, false) as ArtisanSearchResult[];
}

// ---------------------------------------------------------------------------
// Cas 3 : Tous les artisans (pas de filtre spécialité)
// ---------------------------------------------------------------------------
async function fetchAllResults(filters: Parameters<typeof useSearchArtisans>[0]) {
  let q = supabase
    .from("artisan_profiles")
    .select("*")
    .eq("is_active", true)
    .eq("availability", "disponible");
  if (filters.wilaya) q = q.eq("wilaya", filters.wilaya);
  if (filters.commune) q = q.eq("commune", filters.commune);

  const { data: artisans, error } = await q;
  if (error) throw error;
  if (!artisans || artisans.length === 0) return [];

  // Charger les sous-spécialités pour les polyvalents
  const handymanIds = artisans
    .filter((a: any) => a.specialite === "Artisan polyvalent")
    .map((a: any) => a.id);

  const codesByArtisan = new Map<string, string[]>();
  if (handymanIds.length > 0) {
    const { data: hsRows } = await supabase
      .from("artisan_handyman_specialties")
      .select("artisan_id, specialty_code")
      .in("artisan_id", handymanIds);
    (hsRows ?? []).forEach((r: any) => {
      const existing = codesByArtisan.get(r.artisan_id) ?? [];
      existing.push(r.specialty_code);
      codesByArtisan.set(r.artisan_id, existing);
    });
  }

  const mapped = artisans.map((a: any) => ({
    ...a,
    is_polyvalent: a.specialite === "Artisan polyvalent",
    handyman_specialties:
      a.specialite === "Artisan polyvalent"
        ? (codesByArtisan.get(a.id) ?? [])
        : undefined,
  }));

  const withNames = await joinDisplayNames(mapped);
  return processResults(withNames, filters, false) as ArtisanSearchResult[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function joinDisplayNames(artisans: any[]): Promise<any[]> {
  if (artisans.length === 0) return [];
  const userIds = artisans.map((a: any) => a.user_id);
  const { data: profiles } = await supabase
    .from("profiles")
    .select("user_id, display_name")
    .in("user_id", userIds);
  const profileMap = new Map((profiles ?? []).map((p: any) => [p.user_id, p]));
  return artisans.map((row: any) => {
    const profile = profileMap.get(row.user_id);
    return {
      ...row,
      display_name: profile?.display_name || row.raison_sociale || "Artisan",
    };
  });
}

function processResults(
  results: any[],
  filters: { search?: string; sort?: SortOption },
  filteringBySpecialist: boolean
) {
  let filtered = results;

  // Text search
  if (filters.search) {
    const q = filters.search.toLowerCase();
    filtered = filtered.filter(
      (a: any) =>
        a.display_name?.toLowerCase().includes(q) ||
        a.specialite?.toLowerCase().includes(q) ||
        a.raison_sociale?.toLowerCase().includes(q) ||
        a.description?.toLowerCase().includes(q)
    );
  }

  // Sort
  const sort = filters.sort || "relevance";
  filtered.sort((a: any, b: any) => {
    if (sort === "response") {
      return (b.response_rate || 0) - (a.response_rate || 0);
    }
    if (sort === "recent") {
      return (
        new Date(b.last_login_at || 0).getTime() -
        new Date(a.last_login_at || 0).getTime()
      );
    }
    // Relevance : les spécialistes apparaissent avant les polyvalents (+20 points)
    const scoreA = computeScore(a) - (filteringBySpecialist && a.is_polyvalent ? 20 : 0);
    const scoreB = computeScore(b) - (filteringBySpecialist && b.is_polyvalent ? 20 : 0);
    return scoreB - scoreA;
  });

  return filtered as ArtisanSearchResult[];
}

function computeScore(a: any): number {
  let score = 0;
  // Response rate (25%)
  score += ((a.response_rate || 0) / 100) * 25;
  // Response time (15%) - lower is better, cap at 120min
  const rt = Math.min(a.avg_response_time_minutes || 120, 120);
  score += ((120 - rt) / 120) * 15;
  // Profile completion (10%)
  score += ((a.profile_completion_score || 0) / 100) * 10;
  // Recent activity (10%)
  if (a.last_login_at) {
    const days =
      (Date.now() - new Date(a.last_login_at).getTime()) / (1000 * 60 * 60 * 24);
    if (days < 1) score += 10;
    else if (days < 7) score += 6;
    else score += 2;
  }
  // Reactive badge bonus
  if (a.is_reactive_badge) score += 5;
  return score;
}
